import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, Input, Modal, Select, Space, Switch, Tag, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import type { PlatformConnectionRow, PlatformEditPayload, PlatformType } from '@/features/platform/types'
import {
  exchangeBRAccessToken,
  exchangeQuickBooksAccessToken,
  refreshPlatform,
  updatePlatformTokens,
} from '@/features/auth/api'
import {
  clearCredentialFields,
  constructAuthUrl,
  isDisconnectAllowed,
  constructWorkflowMaxAuthUrl,
  openPopup,
  redactedValue,
  requiresApiCredentials,
  requiresKarbonCredentials,
  validatePlatformCredentials,
} from '@/features/platform/utils'
import {
  isQuickBooksCallbackMessage,
  PLATFORM_OAUTH_RESULT,
  type QuickBooksCallbackMessage,
  type QuickBooksResultMessage,
} from '@/features/platform/connectionMessages'
import { createMyobOAuthState, discardMyobOAuthState } from '@/features/platform/myobOAuthState'
import { useAdvicePrepSubscriptionCheck } from '@/features/adviceprep/hooks'
import {
  ADVICEPREP_TRIAL_TOAST_MS,
  advicePrepTrialLimitMessage,
  advicePrepTrialLimitRejection,
} from '@/features/adviceprep/trialLimit'
import { useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'

type BrwfmCallbackMessage = {
  code: string
}

function isBrwfmCallbackMessage(value: unknown): value is BrwfmCallbackMessage {
  if (!value || typeof value !== 'object') return false
  const message = value as Partial<BrwfmCallbackMessage>
  return typeof message.code === 'string' && message.code.length > 0
}

type Props = {
  open: boolean
  row: PlatformConnectionRow | null
  clientId: string
  envName: string
  typeOptions: { value: PlatformType; label: string }[]
  updatePending: boolean
  deletePending: boolean
  onClose: () => void
  onUpdate: (values: PlatformEditPayload) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onDisconnect: (id: string) => Promise<void>
  onRefetch: () => Promise<{ data?: PlatformConnectionRow[] }>
  addToast: (message: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number) => void
}

export function EditPlatformModal({
  open,
  row,
  clientId,
  envName,
  typeOptions,
  updatePending,
  deletePending,
  onClose,
  onUpdate,
  onDelete,
  onDisconnect,
  onRefetch,
  addToast,
}: Props) {
  const [form] = Form.useForm<PlatformEditPayload>()
  const session = useAuthSession()
  const [editSecretsVisible, setEditSecretsVisible] = useState(false)
  const [isProcessingAuth, setIsProcessingAuth] = useState(false)
  const popupRef = useRef<Window | null>(null)
  const popupCloseIntervalRef = useRef<number | null>(null)
  // Guards against a duplicate relay message triggering the exchange twice.
  // OAuth auth codes are single-use, so a second exchange would fail with a 400.
  const oauthInProgressRef = useRef(false)
  const authCompletionHandledRef = useRef(false)
  const pendingMyobStateRef = useRef<string | null>(null)
  const advicePrepBillingToggledRef = useRef(false)

  const clearPendingMyobState = useCallback(() => {
    discardMyobOAuthState(pendingMyobStateRef.current)
    pendingMyobStateRef.current = null
  }, [])

  useEffect(() => {
    if (!open || !row) return
    setEditSecretsVisible(false)
    setIsProcessingAuth(false)
    authCompletionHandledRef.current = false
    advicePrepBillingToggledRef.current = false
    form.setFieldsValue({
      id: row.id,
      name: row.name,
      type: row.type,
      apiKey: row.apiKey,
      apiSecret: row.apiSecret,
      username: row.username,
      password: row.password,
      account: row.account,
      warehouse: row.warehouse,
      supportAdvisePrep: row.supportAdvisePrep,
    })
  }, [form, open, row])

  const clearPopupCloseInterval = useCallback(() => {
    if (popupCloseIntervalRef.current) {
      window.clearInterval(popupCloseIntervalRef.current)
      popupCloseIntervalRef.current = null
    }
  }, [])

  const closePopup = useCallback(() => {
    clearPopupCloseInterval()
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close()
    popupRef.current = null
    clearPendingMyobState()
  }, [clearPendingMyobState, clearPopupCloseInterval])

  const close = useCallback(() => {
    closePopup()
    setEditSecretsVisible(false)
    setIsProcessingAuth(false)
    form.resetFields()
    onClose()
  }, [closePopup, form, onClose])

  useEffect(
    () => () => {
      closePopup()
    },
    [closePopup]
  )

  const handlePopupClosed = useCallback(async (type: PlatformType) => {
    if (authCompletionHandledRef.current || oauthInProgressRef.current) return

    clearPopupCloseInterval()
    popupRef.current = null
    clearPendingMyobState()

    if (type === 'BRWFM' || type === 'QuickBooks') {
      return
    }

    authCompletionHandledRef.current = true
    try {
      const refreshed = await onRefetch()
      const myobConnected = refreshed.data?.some(
        (platform) => platform.id === row?.id && platform.status === 'Connected',
      )
      if (type === 'MYOB' && myobConnected) {
        addToast('MYOB connected successfully.', 'success')
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to refresh platform status'
      addToast(message, 'error')
    } finally {
      close()
    }
  }, [addToast, clearPendingMyobState, clearPopupCloseInterval, close, onRefetch, row?.id])

  useEffect(() => {
    if (!row || !clientId) return

    const completeAuth = async () => {
      authCompletionHandledRef.current = true
      await onRefetch()
    }

    const handleBrwfmCallback = async (callback: BrwfmCallbackMessage) => {
      try {
        addToast('Connecting WorkflowMax platform...', 'info')
        const token = await exchangeBRAccessToken(callback.code)
        if (!token?.access_token) throw new Error('Unable to exchange WorkflowMax auth code')
        await updatePlatformTokens({
          accessToken: token.access_token,
          refreshToken: token.refresh_token || '',
          expireIn: token.expires_in || 0,
          platformId: row.id,
          clientId,
        })
        await refreshPlatform(row.id)
        localStorage.removeItem('platformId')
        addToast('WorkflowMax connected successfully.', 'success')
        await completeAuth()
        close()
      } catch (e) {
        const message = e instanceof Error && e.message ? e.message : 'Unable to complete authentication'
        addToast(message, 'error')
      } finally {
        clearPopupCloseInterval()
        if (popupRef.current && !popupRef.current.closed) popupRef.current.close()
        popupRef.current = null
      }
    }

    const postQuickBooksResult = (result: QuickBooksResultMessage) => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.postMessage(result, window.location.origin)
      }
    }

    const handleQuickBooksCallback = async (callback: QuickBooksCallbackMessage) => {
      try {
        // The exchange endpoint saves the tokens server-side, so a successful
        // response means the connection is stored — no updatePlatformTokens.
        addToast('Connecting QuickBooks platform...', 'info')
        await exchangeQuickBooksAccessToken(callback.code, callback.realmId, row.id, clientId)
        await refreshPlatform(row.id)
        addToast('QuickBooks connected successfully.', 'success')
        await completeAuth()
        postQuickBooksResult({
          type: PLATFORM_OAUTH_RESULT,
          platform: 'QuickBooks',
          status: 'success',
        })
        popupRef.current = null
        close()
      } catch (e) {
        const fallback =
          'Unable to connect QuickBooks. The authorization may have expired or the connection is misconfigured — please try again.'
        const message = e instanceof Error && e.message ? e.message : fallback
        addToast(message, 'error')
        postQuickBooksResult({
          type: PLATFORM_OAUTH_RESULT,
          platform: 'QuickBooks',
          status: 'error',
          error: message,
        })
      } finally {
        if (authCompletionHandledRef.current) clearPopupCloseInterval()
      }
    }

    const listener = async (event: MessageEvent) => {
      const values = form.getFieldsValue(true)
      if (values.type !== 'BRWFM' && values.type !== 'QuickBooks') return

      if (event.origin !== window.location.origin || event.source !== popupRef.current) return
      if (oauthInProgressRef.current) return

      if (values.type === 'BRWFM' && !isBrwfmCallbackMessage(event.data)) return
      if (values.type === 'QuickBooks' && !isQuickBooksCallbackMessage(event.data)) return

      oauthInProgressRef.current = true
      setIsProcessingAuth(true)

      try {
        if (values.type === 'BRWFM') {
          await handleBrwfmCallback(event.data)
        } else {
          await handleQuickBooksCallback(event.data)
        }
      } finally {
        oauthInProgressRef.current = false
        setIsProcessingAuth(false)
      }
    }

    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [addToast, clearPopupCloseInterval, clientId, close, form, onRefetch, row])

  const onEditTypeChange = (nextType: PlatformType) => {
    form.setFieldsValue(clearCredentialFields(nextType))
  }

  // The grid now sends supportAdvisePrep to everyone so the trial limit can be
  // counted, so its presence no longer identifies a DI admin. Check the user.
  const canEditAdvicePrepBilling = open && session?.currentUserTypeId === UserTypes.Admin
  const advicePrepSubscription = useAdvicePrepSubscriptionCheck(clientId, canEditAdvicePrepBilling)
  // Shown but disabled while checking or after a failed check: hiding it outright
  // makes a billing outage look like the client having no subscription.
  const advicePrepBillingLocked = !advicePrepSubscription.hasSubscription
  const showAdvicePrepBilling =
    canEditAdvicePrepBilling &&
    (advicePrepSubscription.hasSubscription || advicePrepSubscription.isChecking || advicePrepSubscription.isError)

  const readEditValues = async (): Promise<PlatformEditPayload> => {
    const values = await form.validateFields()
    if (!advicePrepBillingToggledRef.current) return values
    // validateFields skips unmounted fields, and a recheck finding no subscription unmounts the switch.
    return { ...values, supportAdvisePrep: form.getFieldValue('supportAdvisePrep') }
  }

  const editType = Form.useWatch('type', form) as PlatformType | undefined
  const showApiFields = !!editType && requiresApiCredentials(editType)
  const showKarbonFields = !!editType && requiresKarbonCredentials(editType)
  const canToggleSecrets = editType === 'Karbon' || editType === 'FYI'
  const isNonAuthType = editType === 'Karbon' || editType === 'FYI'
  const shouldMaskSecrets = canToggleSecrets && !editSecretsVisible

  const confirmEdit = async () => {
    try {
      const values = await readEditValues()
      const credentialError = validatePlatformCredentials(values)
      if (credentialError) {
        addToast(credentialError, 'warning')
        return
      }
      await onUpdate(values)
      addToast('Platform updated', 'success')
      close()
    } catch (e) {
      // Putting a connection on the AdvicePrep meter spends the trial
      // allowance, so this save hits the same cap the create flow does. A
      // policy answer, not a failure, and retrying cannot succeed.
      const trialLimit = advicePrepTrialLimitRejection(e)
      if (trialLimit) {
        addToast(advicePrepTrialLimitMessage(trialLimit), 'warning', ADVICEPREP_TRIAL_TOAST_MS)
        close()
        return
      }

      const message = e instanceof Error ? e.message : 'Unable to save platform'
      addToast(message, 'error')
    }
  }

  const confirmDelete = () => {
    if (!row) return
    Modal.confirm({
      title: 'Delete platform',
      content: `Are you sure you want to delete ${row.name}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await onDelete(row.id)
          await onRefetch()
          addToast('Platform deleted successfully', 'success')
          close()
        } catch (e) {
          const message = e instanceof Error ? e.message : 'Unable to delete platform'
          addToast(message, 'error')
          throw e
        }
      },
    })
  }

  const confirmDisconnect = () => {
    if (!row) return
    Modal.confirm({
      title: 'Disconnect platform',
      content: `Are you sure you want to disconnect ${row.name}?`,
      okText: 'Disconnect',
      okButtonProps: { danger: true },
      onOk: async () => {
        await onDisconnect(row.id)
        addToast(`${row.name} disconnected`, 'warning')
        close()
      },
    })
  }

  const authenticate = async () => {
    if (!row) return
    try {
      const values = await readEditValues()
      const credentialError = validatePlatformCredentials(values)
      if (credentialError) {
        addToast(credentialError, 'warning')
        return
      }

      const nameChanged = values.name !== row.name
      const typeChanged = values.type !== row.type
      if (nameChanged || typeChanged) {
        await onUpdate(values)
      }

      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      if (values.type === 'MYOB') clearPendingMyobState()
      const myobState = values.type === 'MYOB' ? createMyobOAuthState(values.id || row.id) : undefined
      if (myobState) pendingMyobStateRef.current = myobState
      const authUrl =
        values.type === 'BRWFM'
          ? constructWorkflowMaxAuthUrl()
          : constructAuthUrl(values.type, values.id || row.id, envName, origin, myobState)
      if (!authUrl) {
        clearPendingMyobState()
        addToast('Authentication URL is not configured for this platform', 'error')
        return
      }

      if (values.type === 'BRWFM') {
        localStorage.setItem('platformId', values.id || row.id)
      }

      const popup = openPopup(authUrl, 600, 500)
      if (!popup) {
        clearPendingMyobState()
        setIsProcessingAuth(false)
        addToast('Popup blocked. Please allow popups and retry.', 'error')
        return
      }
      popupRef.current = popup

      addToast('Complete authentication in the popup window.', 'info')

      popupCloseIntervalRef.current = window.setInterval(() => {
        if (!popup.closed) return
        void handlePopupClosed(values.type)
      }, 500)
    } catch (e) {
      clearPendingMyobState()
      setIsProcessingAuth(false)
      const message = e instanceof Error ? e.message : 'Unable to start authentication'
      addToast(message, 'error')
    }
  }

  return (
    <Modal
      open={open}
      title="Edit platform"
      onCancel={close}
      footer={
        <Space>
          {canToggleSecrets && (
            <Button onClick={() => setEditSecretsVisible((prev) => !prev)}>
              {editSecretsVisible ? 'Hide Secrets' : 'Edit Secrets'}
            </Button>
          )}
          <Button type="primary" loading={updatePending} onClick={() => void confirmEdit()}>
            Update
          </Button>
          {!isNonAuthType && !row?.isAuthenticated && (
            <Button loading={isProcessingAuth} onClick={() => void authenticate()}>
              Authenticate
            </Button>
          )}
          {row && isDisconnectAllowed(row) && <Button onClick={confirmDisconnect}>Disconnect</Button>}
          <Button danger icon={<DeleteOutlined />} loading={deletePending} onClick={confirmDelete}>
            Delete
          </Button>
          <Button onClick={close}>Cancel</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        {isProcessingAuth && (editType === 'BRWFM' || editType === 'QuickBooks') && (
          <Alert
            className="mb-4"
            type="info"
            showIcon
            message={editType === 'QuickBooks' ? 'Connecting QuickBooks' : 'Connecting WorkflowMax'}
            description="Your authentication has been received. We are saving the connection and refreshing the platform now."
          />
        )}
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="type" label="Platform Type" rules={[{ required: true, message: 'Platform type is required' }]}>
          <Select options={typeOptions} onChange={onEditTypeChange} />
        </Form.Item>
        {showAdvicePrepBilling && (
          <Form.Item
            name="supportAdvisePrep"
            label={
              <span className="flex items-center gap-2">
                Billed in AdvicePrep
                <Tag color="blue" className="m-0 rounded-full !text-xs">
                  Admin
                </Tag>
              </span>
            }
            valuePropName="checked"
            extra={
              advicePrepSubscription.isChecking
                ? 'Checking this client’s AdvicePrep subscription…'
                : advicePrepSubscription.isError
                  ? 'Could not check this client’s AdvicePrep subscription, so this cannot be changed right now.'
                  : 'Turning it off takes this connection off the AdvicePrep meter.'
            }
          >
            <Switch
              disabled={advicePrepBillingLocked}
              loading={advicePrepSubscription.isChecking}
              onChange={() => {
                advicePrepBillingToggledRef.current = true
              }}
            />
          </Form.Item>
        )}
        {showApiFields && shouldMaskSecrets ? (
          <Space direction="vertical" size={2} className="w-full">
            <Typography.Text type="secondary">ApiKey</Typography.Text>
            <Typography.Text>{redactedValue(form.getFieldValue('apiKey'))}</Typography.Text>
            <Typography.Text type="secondary" className="mt-2">
              ApiSecret
            </Typography.Text>
            <Typography.Text>{redactedValue(form.getFieldValue('apiSecret'))}</Typography.Text>
          </Space>
        ) : null}
        {showApiFields && !shouldMaskSecrets && (
          <>
            <Form.Item name="apiKey" label="API Key" rules={[{ required: true, message: 'API Key is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="apiSecret" label="API Secret" rules={[{ required: true, message: 'API Secret is required' }]}>
              <Input />
            </Form.Item>
          </>
        )}

        {showKarbonFields && shouldMaskSecrets ? (
          <Space direction="vertical" size={2} className="w-full">
            <Typography.Text type="secondary">Username</Typography.Text>
            <Typography.Text>{redactedValue(form.getFieldValue('username'))}</Typography.Text>
            <Typography.Text type="secondary" className="mt-2">
              Password
            </Typography.Text>
            <Typography.Text>{redactedValue(form.getFieldValue('password'))}</Typography.Text>
            <Typography.Text type="secondary" className="mt-2">
              Account
            </Typography.Text>
            <Typography.Text>{redactedValue(form.getFieldValue('account'))}</Typography.Text>
            <Typography.Text type="secondary" className="mt-2">
              Warehouse
            </Typography.Text>
            <Typography.Text>{redactedValue(form.getFieldValue('warehouse'))}</Typography.Text>
          </Space>
        ) : null}
        {showKarbonFields && !shouldMaskSecrets && (
          <>
            <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Username is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="account" label="Account Name" rules={[{ required: true, message: 'Account is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="warehouse" label="Warehouse" rules={[{ required: true, message: 'Warehouse is required' }]}>
              <Input />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}
